import { useCallback, useEffect, useState } from "react";
import { Spin, Steps } from "antd";
import { Icon } from 'Components';
import Step1 from "./CampaignSteps/Step1";
import Step2TestConfiguration from "./CampaignSteps/Step2TestConfiguration";
import Step4DeliverySchedule from "./CampaignSteps/Step4DeliverySchedule";
import Step3Recipients from "./CampaignSteps/Step3Recipients";
import Step5ReviewStart from "./CampaignSteps/Step5ReviewStart";
import { CampaignThresholdData } from "./PhishingManager";
import ExitCampaignModal from "./CampaignSteps/Modals/ExitCampaignModal";
import { CreateCampaignStepOnePayload, createPhishingCampaignStepOne, createPhishingCampaignStepTwo, getPhishingCampaignFilters, getPhishingCampaignTemplatePreviewDetails, launchPhishingCampaign } from "Services/Api/PhishingManager/PhishingManager.Service";
import PhishingModal from "./CampaignSteps/Modals/PhishingModal";
import { useIsMobile } from "Hooks";
import Header from "./components/Header";
import { DESKTOP_STEPS, MOBILE_STEPS } from "./PhishingManager.constants";
import { errorMessageHandler } from "app-util";
const { Step } = Steps;

type CampaignSetupProps = {
  tenantId: number | string | undefined;
  onBack: () => void;
  thresholdData: null | CampaignThresholdData
  errorMessage?: string;
  setErrorMessage: (message: string) => void;
};

export interface FilterResults {
  languages: { id: number, name: string }[],
  difficulties: string[],
  categories: { id: number, name: string }[],
}

export interface TemplateLanguage {
  id: number;
  name?: string;
}

export interface TemplateDetails {
  id: number;
  name: string;
  categories: { id: number, name: string }[];
  description: string | null;
  difficulty: "beginner" | "intermediate" | "advanced" | string;
  email_template_html: string;
  landing_page_html: string;
  image_path: string;
  selected_packaged_template_language_id: number;
  languages: TemplateLanguage[];
}

export default function CampaignSetup({ onBack, tenantId, thresholdData, errorMessage, setErrorMessage }: CampaignSetupProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [showPhisingModal, setShowPhisingModal] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [campaignId, setCampaignId] = useState<string | number | null>(null);
  const [filterResults, setFilterResults] = useState<FilterResults | null>(null);
  const [templateDetails, setTemplateDetails] = useState<TemplateDetails | null>(null);
  const isMobile = useIsMobile();
  const STEPS = isMobile ? MOBILE_STEPS : DESKTOP_STEPS;
  const toggleExitModal = useCallback((show: boolean) => setShowExitModal(show), [])
  const [step1State, setStep1State] = useState({
    search: "",
    language: "",
    category: "",
    difficulty: "",
    templatePage: 1,
  });
  const [stepErrorMessage, setStepErrorMessage] = useState<string | null>(null);
  const [showConfirmBtn, setShowConfirmBtn] = useState(true); 

  const handleNext = async () => {
    if (currentStep > 1) {
      setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1))
      return;
    }
    if (!tenantId || !templateDetails?.id || !templateDetails?.selected_packaged_template_language_id) return;
    setIsLoading(true);
    try {
      const payload: CreateCampaignStepOnePayload = {
        venza_tenant_id: Number(tenantId),
        packaged_template_id: Number(templateDetails?.id),
        language_id: Number(templateDetails?.selected_packaged_template_language_id),
      };
      if (!!campaignId) {
        payload["campaign_id"] = Number(campaignId);
      }
      const response = await createPhishingCampaignStepOne(payload);
      setCampaignId(response.data.campaign_id);
      setCurrentStep((s) => Math.min(s + 1, 4));
    } catch (err: any) {
      const errorMessage = errorMessageHandler(err, "Failed to create campaign step 2");
      setTemplateDetails(null);
      setSelectedTemplate(null);
      setErrorMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhishingModalClose = async () => {
    setShowPhisingModal(false);
    setTemplateDetails(null);
    setSelectedTemplate(null);
    setShowConfirmBtn(true);
  };

  const resetCampaignState = () => {
    setCurrentStep(0);
    setSelectedTemplate(null);
    setCampaignId(null);
    setTemplateDetails(null);
    setFilterResults(null);
    setShowPhisingModal(false);
    setShowExitModal(false);
    setStep1State({
      search: "",
      language: "",
      category: "",
      difficulty: "",
      templatePage: 1,
    });
    onBack();
  };

  const handleLaunchCampaign = async () => {
    setIsLoading(true);
    try {
      await launchPhishingCampaign({
        venza_tenant_id: 43784,
        campaign_id: 63
      });
      alert("Campaign launched successfully!");
      resetCampaignState();
    } catch (err: any) {
      const errorMessage = errorMessageHandler(err, "Failed to launch campaign");
      setErrorMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }

  const handleNext2 = async (emails: string[]) => {
    if (!tenantId || !templateDetails?.id || !emails?.length) return;
    setIsLoading(true);
    try {
      await createPhishingCampaignStepTwo({
        venza_tenant_id: Number(tenantId),
        campaign_id: Number(campaignId),
        emails: emails,
      });
      setCurrentStep((s) => Math.min(s + 1, 4))
    } catch (err: any) {
      const errorMessage = errorMessageHandler(err, "Failed to create campaign step 2");
      setStepErrorMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if(currentStep == 1) {
      setSelectedTemplate(null);
      setTemplateDetails(null);
    }
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  const handlePreviewTemplate = async (id: string | number | null) => {
    if (!!id) {
      setSelectedTemplate(id);
      try {
        setShowPhisingModal(true);
        if(id == templateDetails?.id) return;
        setIsLoading(true);
        const response =
          await getPhishingCampaignTemplatePreviewDetails(
            tenantId,
            id ?? "",
          );
        setTemplateDetails(response.data);
      } catch (err: any) {
        const errorMessage = errorMessageHandler(err, "Failed to get preview template details");
        setErrorMessage(errorMessage);
        setShowPhisingModal(false);
        setTemplateDetails(null);
        setSelectedTemplate(null);
      } finally {
        setIsLoading(false);
      }
    } else {
      setShowPhisingModal(false);
      setTemplateDetails(null);
      setSelectedTemplate(null);
    }
  }

  const loadPreviewDetails = async (langId: string | number | null) => {
    setIsLoading(true);
    try {
      const response =
        await getPhishingCampaignTemplatePreviewDetails(
          tenantId,
          templateDetails?.id ?? "",
          langId ?? ""
        );
      setTemplateDetails(response.data);
    } catch (err: any) {
      const errorMessage = errorMessageHandler(err, "Unable to load preview details");
      setErrorMessage(errorMessage);
      setTemplateDetails(null);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    const fetchFilterCampaigns = async (value?: string) => {
      setIsLoading(true);
      try {
        const response = await getPhishingCampaignFilters(tenantId, value ?? "");
        setFilterResults(response.data)
      } catch (error: any) {
        setFilterResults(null)
      } finally {
        setIsLoading(false);
      }
    };
    fetchFilterCampaigns();
  }, []);

  return (
    <Spin spinning={isLoading}>
      <div className="campaign-step-up">
        <Header title="Campaign setup" description="Follow the steps to create a new campaign">
          <div className="close-icon-wrapper" onClick={() => toggleExitModal(true)}>
            <Icon type="campaignSetupClose" />
          </div>
        </Header>

        <div className="campaign-step-track-container">
          <div className="phishing-step1">
            <Steps current={currentStep} className="mx-auto d-flex" size="small" labelPlacement="vertical">
              {STEPS.map((label, i) => (
                <Step key={i} status={i < currentStep ? "finish" : i === currentStep ? "process" : "wait"}
                  className="d-flex justify-center"
                  style={{ cursor: i <= currentStep ? "pointer" : "default" }}
                  title={label} />))}
            </Steps>
          </div>
        </div>

        <div className="step-card-section">
          <div style={{ display: currentStep === 0 ? "block" : "none", width: "100%" }}>
            <Step1
              tenantId={tenantId}
              selectedTemplate={selectedTemplate}
              thresholdData={thresholdData}
              filterResults={filterResults}
              onSelect={handlePreviewTemplate}
              onNext={handleNext}
              setErrorMessage={setErrorMessage}
              onClose={onBack}
              step1State={step1State}
              setStep1State={setStep1State}
            />
          </div>

          <div style={{ display: currentStep === 1 ? "block" : "none", width: "100%" }}>
            <Step2TestConfiguration
              setStepErrorMessage={setStepErrorMessage}
              stepErrorMessage={stepErrorMessage}
              onBack={handleBack}
              onNext={handleNext2}
            />
          </div>

          <div style={{ display: currentStep === 2 ? "block" : "none", width: "100%" }}>
            <Step3Recipients
              onBack={handleBack}
              onNext={handleNext}
            />
          </div>

          <div style={{ display: currentStep === 3 ? "block" : "none", width: "100%" }}>
            <Step4DeliverySchedule
              tenantId={tenantId}
              campaignId={campaignId}
              setErrorMessage={setErrorMessage}
              onBack={handleBack}
              onNext={handleNext}
            />
          </div>

          <div style={{ display: currentStep === 4 ? "block" : "none", width: "100%" }}>
            <Step5ReviewStart
              onBack={handleBack}
              onFinish={handleLaunchCampaign}
              showPreview={(id: number | string) => {
                setShowConfirmBtn(false);
                handlePreviewTemplate(id);
              }}
            />
          </div>
        </div>
      </div>
      <ExitCampaignModal open={showExitModal} onClose={() => toggleExitModal(false)} onConfirm={onBack} />
      <PhishingModal
        showConfirmBtn={showConfirmBtn}
        loading={isLoading}
        visible={showPhisingModal}
        templateDetails={templateDetails}
        onClose={handlePhishingModalClose}
        onNext={() => {
          handleNext();
          setShowPhisingModal(false);
        }}
        onLanguageChange={(langId: number) => {
          loadPreviewDetails(langId)
        }}
      />
    </Spin>
  );
}
